/**
 * Centralized AI behavior configuration
 * This ensures consistent behavior across all AI providers (Gemini, OpenAI, Anthropic)
 */
import { POPULAR_ICONS } from '../constants';
import { getSelectedModel } from './apiKeyService';

export interface AIBehaviorConfig {
  prompts: {
    initial: string;
    append: string;
  };
  temperature: {
    initial: number;
    append: number;
  };
  model: {
    gemini: string;
    openai: string;
    anthropic: string;
  };
}

/**
 * The main prompt template for initial diagram generation
 */
const INITIAL_PROMPT_TEMPLATE = `You are an expert software architect drawing ONE clear story about: "{{prompt}}".

The diagram must answer a question, not catalogue a system. A reader should see the primary path within seconds.

COMPOSITION RULES (highest priority — obey before anything else):
1. Define the single primary path a request or data flow takes through this system, end to end (e.g. user -> CDN -> API -> queue -> worker -> database). Every primary node sits on or hangs off that path.
2. HARD CAP: at most 12 primary nodes. If the system has more parts, choose the 12 that tell the story and fold the rest into node descriptions. NEVER exceed 12 nodes.
3. Side branches attach to the nearest main-path node. No floating clusters, no unconnected islands.
4. PRUNE EDGES: every edge must carry real meaning (request flow, data movement, auth, deployment). Delete low-value edges instead of styling or labeling them. A sparse, readable diagram beats a complete, tangled one.
5. Label edges sparingly: only when the protocol, action, or direction is not obvious from the endpoints (e.g. "gRPC", "publishes events", "reads replica"). Never label every edge.

NODES:
- Use namespaced icons: 'si:react' for SimpleIcons, 'mdi:server' for Material Design, 'carbon:bare-metal-server' for Carbon
- PREFER a verified id from this reference (Technology=id). For anything not listed, use a plausible lowercase brand slug like 'si:<brand>' (e.g. 'si:temporal'); use 'logos:<name>' for AWS/GCP/Azure services (e.g. 'logos:aws-lambda'):
{{iconReference}}
- Assign ONE category (frontend, backend, database, infrastructure, devops, security, monitoring, storage, network, cloud)
- Importance: 1 ONLY for nodes on the primary path, 2-3 for critical support (cache, auth, load balancer), 4-5 for peripherals. This is visual hierarchy, not a quality rating.
- Put version/status detail in metadata, not in the label.

GROUPS:
- One group per logical layer or bounded context ONLY when it clarifies the story (e.g. "Frontend", "API Layer", one per microservice). 2-6 groups maximum.
- Leave small or simple diagrams ungrouped. Every id in a group's nodeIds MUST be a real node id.

LAYOUT HINTS (every node needs a layout object — this is how you control the visual arrangement):
- Picture the primary path running straight down the page in numbered LAYERS: layer 0 is the entry point (user/client/gateway), and each hop downstream increments the layer by 1.
- Set layout.mainPath=true ONLY for nodes on that spine. Exactly one main-path node per layer it passes through.
- Side branches and support components take the layer of the main-path node they serve, with layout.mainPath=false and a layout.orderInLayer slot (0 first).
- Layers must not all be identical — a diagram with every node on layer 0-1 carries no spatial information. Use the full depth of the story.

EDGES:
- Set edge type honestly: data, api, event, dependency, control. Mark bidirectional connections.
- Do NOT set sourceHandle or targetHandle — connection points are derived automatically from the final layout.
- No self-loops. No duplicate edges between the same pair.

ANNOTATIONS (use sparingly):
- stickyNotes ONLY for important warnings or critical operational notes.
- textNodes ONLY for section headers in complex diagrams — never for the diagram title.

REPLY:
- Include a top-level "notes" string: 1-2 plain sentences summarizing the architecture and its primary flow for the user, without node counts or layout scores.

VALIDATION (before returning):
- Every node id referenced by an edge or group exists.
- Icon namespaces are valid.
- Total node count is 12 or fewer.

Return valid JSON matching the schema.`;

/**
 * The prompt template for append mode (adding to existing diagrams)
 */
const APPEND_PROMPT_TEMPLATE = `You are adding to an EXISTING tech stack diagram. Generate ONLY NEW complementary components for: "{{prompt}}".

COMPOSITION RULES (highest priority):
1. Add at most 6 new nodes per request. Prefer fewer — ask yourself what the existing diagram is MISSING, not what else the system has.
2. New nodes must connect to the existing architecture through their exact IDs. No floating clusters.
3. PRUNE: do not add an edge unless it carries real meaning (request flow, data movement, auth, deployment).
4. Label new edges sparingly — only non-obvious protocols, actions, or directions.

EXISTING DIAGRAM:
Nodes:
{{nodes}}

Connections:
{{edges}}

Groups:
{{groups}}

Sticky Notes:
{{stickyNotes}}
{{contextSummary}}

REQUIREMENTS:
1. Generate ONLY NEW nodes that complement the existing architecture. DO NOT regenerate existing nodes.
2. Use unique IDs with prefix "new_" (e.g., "new_redis_abc123").
3. Use namespaced icons: 'si:react' for SimpleIcons, 'mdi:server' for Material Design, 'carbon:bare-metal-server' for Carbon. PREFER a verified id from this reference (Technology=id); for anything not listed use a plausible 'si:<brand>' or 'logos:<name>':
{{iconReference}}
4. Assign category and importance consistent with existing components (1 = on the primary path).
5. Give each new node a layout object with layer/orderInLayer/mainPath describing where it sits in the NEW cluster's own flow (entry to that cluster = layer 0); the new cluster is placed below the existing diagram.
6. You CAN create edges connecting new nodes TO existing nodes using their exact IDs.
7. DO NOT create groups - let users organize manually if needed.
8. Set edge type honestly (data, api, event, dependency, control).
9. Do NOT set sourceHandle or targetHandle — connection points are derived automatically from layout.
10. Add sticky notes ONLY for important warnings about the new components.
11. Match the visual style and organization of the existing diagram.
12. Include a top-level "notes" string telling the user what you added and why in 1-2 plain sentences.

Return JSON with only the NEW elements to add.`;

/**
 * The prompt template for conversational revision: the model receives the
 * current diagram in the exact output schema plus a change request, and must
 * return the FULL updated stack with ids kept stable — the merge
 * (services/aiRevise.ts) matches its output to the canvas by id.
 */
const REVISE_PROMPT_TEMPLATE = `You are revising an EXISTING tech stack diagram. Apply the change request and return the FULL updated diagram.

CHANGE REQUEST: "{{instruction}}"

CURRENT DIAGRAM (JSON — the same schema you must return):
{{currentStack}}

REVISION RULES (highest priority — obey before anything else):
1. ID STABILITY IS SACRED. Keep the exact id of every node, group, sticky note, and text node you do not delete. The client merges your output into the user's canvas by id — renaming an id destroys that element's hand-placed position.
2. Return the FULL stack, not a diff: every unchanged node, edge, group, sticky note, and text node must appear in your output. Anything absent is treated as deleted — so only omit what the change request removes.
3. Add at most 6 NEW nodes per request. Prefer fewer — apply the change, don't rebuild the diagram.
4. New node ids must be unique, descriptive, and must NOT collide with any existing id (prefix them "new_", e.g. "new_redis").
5. Preserve the layout object (layer/orderInLayer/mainPath) of unchanged nodes verbatim. Give NEW nodes sensible layout hints that extend the existing layer structure (entry point = layer 0, each hop downstream +1, mainPath=true only on the primary path).
6. Keep the diagram's composition contract: one readable primary path, at most 12 primary nodes, no floating clusters, no unconnected islands.

EDITING THE DETAILS:
- You may change labels, descriptions, icons, categories, importance, group membership, and edges — when the change request calls for it.
- New or replacement icons: use namespaced ids ('si:react', 'mdi:server', 'carbon:bare-metal-server'). PREFER a verified id from this reference (Technology=id); for anything not listed use a plausible 'si:<brand>' or 'logos:<name>':
{{iconReference}}
- Set edge type honestly: data, api, event, dependency, control. Mark bidirectional connections.
- Do NOT set sourceHandle or targetHandle — connection points are derived automatically from the final layout.
- No self-loops. No duplicate edges between the same pair. Label edges sparingly.
- Groups: every id in a group's nodeIds MUST be a real node id. You may create, rename, repopulate, or delete groups when asked.
- stickyNotes ONLY for important warnings; textNodes ONLY for section headers. Keep existing ones unless asked to remove them.

REPLY:
- Include a top-level "notes" string telling the user what you changed and why in 1-2 plain sentences, without node counts or layout scores.

VALIDATION (before returning):
- Every id referenced by an edge or group exists in your output.
- Unchanged nodes keep their original ids and layout hints.

Return valid JSON matching the schema of the current diagram.`;

/**
 * Build the revise prompt for conversational diagram editing.
 * @param instruction - The user's change request ("add a Redis cache…").
 * @param currentStackJson - canvasToGeneratedStack output, JSON-stringified.
 */
export function buildRevisePrompt(instruction: string, currentStackJson: string): string {
  return REVISE_PROMPT_TEMPLATE.replace('{{instruction}}', instruction)
    .replace('{{currentStack}}', currentStackJson)
    .replace('{{iconReference}}', ICON_REFERENCE);
}

/**
 * The patch prompt for conversational diagram editing: the model returns ONLY
 * the delta (services/aiPatch.ts applies it), never the full diagram.
 * Absence is a no-op — the inversion of the full-stack revise contract.
 */
const PATCH_PROMPT_TEMPLATE = `You are editing an existing tech stack diagram. Respond with a PATCH — ONLY the changes the request calls for, never the whole diagram.

CHANGE REQUEST: "{{instruction}}"

CURRENT DIAGRAM (JSON — ids, fields and layout hints refer to this):
{{currentStack}}

OUTPUT SHAPE — include ONLY the keys you need (omit everything else):
{
  "notes": "1-2 plain sentences telling the user what you changed and why",
  "addNodes": [{ "id": "new_redis", "label": "Redis", "icon": "si:redis", "description": "Cache", "category": "database", "importance": 2, "groupId": "optional_existing_group_id", "layout": { "layer": 1, "orderInLayer": 1, "mainPath": false } }],
  "updateNodes": [{ "id": "an_existing_id", "label": "Renamed", "icon": "si:newicon", "description": "New role", "importance": 1 }],
  "removeNodeIds": ["an_existing_id"],
  "addEdges": [{ "source": "existing_or_new_id", "target": "existing_or_new_id", "label": "reads", "type": "data", "bidirectional": false }],
  "removeEdges": [{ "source": "id_a", "target": "id_b", "label": "only needed when the pair has several connections" }],
  "addGroups": [{ "id": "new_g_stack", "label": "Edge stack", "nodeIds": ["existing_id", "new_redis"] }],
  "updateGroups": [{ "id": "existing_group_id", "label": "Renamed", "nodeIds": ["full", "membership", "list"] }],
  "removeGroupIds": ["existing_group_id"],
  "addStickyNotes": [{ "id": "new_note_1", "label": "Warning", "color": "yellow" }],
  "addTextNodes": [{ "id": "new_text_1", "label": "Section header" }]
}

PATCH RULES (highest priority):
1. ABSENCE IS SAFE. Anything you do not mention stays exactly as it is. Do NOT re-emit unchanged nodes — that is what the old protocol demanded; this one forbids it.
2. ID STABILITY IS SACRED. updateNodes/removeNodeIds/addEdges/group nodeIds must reference existing ids verbatim, copied from the current diagram. Renaming an id destroys the user's hand-placed layout.
3. addNodes: at most 6, with unique fresh ids prefixed "new_" that collide with nothing. Give each sensible layout hints that extend the existing layer structure (entry point = layer 0, each hop downstream +1, mainPath=true only on the primary path).
4. addEdges endpoints must exist after your patch (existing ids or ids you add in this patch). No self-loops, no duplicate connections between the same pair.
5. Removing a node removes its connections automatically — do not also list its edges in removeEdges.
6. updateGroups REPLACES membership with the full list you provide. removeGroupIds dissolves a group; its members stay on the canvas.
7. Every change must trace to the change request. Do not "improve", rename or re-icon unrelated parts.
8. Keep the composition contract: one readable primary path, at most 12 primary nodes, no floating clusters, no unconnected islands.
9. Icons: namespaced ids ('si:react', 'mdi:server', 'carbon:bare-metal-server'). PREFER a verified id from this reference (Technology=id); for anything not listed use a plausible 'si:<brand>' or 'logos:<name>':
{{iconReference}}

VALIDATION (before returning):
- Every id you reference exists either in the current diagram or in your own addNodes.
- notes must describe only what this patch actually does.

Return ONLY the patch as valid JSON — no markdown, no commentary.`;

/**
 * Build the patch prompt for conversational diagram editing.
 * @param instruction - The user's change request ("add a Redis cache…").
 * @param currentStackJson - canvasToGeneratedStack output, JSON-stringified.
 */
export function buildPatchPrompt(instruction: string, currentStackJson: string): string {
  return PATCH_PROMPT_TEMPLATE.replace('{{instruction}}', instruction)
    .replace('{{currentStack}}', currentStackJson)
    .replace('{{iconReference}}', ICON_REFERENCE);
}

/**
 * The prompt for the focused repair round (STN-71): the model gets its own
 * previous JSON plus measured diagnostics and repairs ONLY the named subjects
 * — Archify's bounded correction loop, adapted. Never a blind regenerate.
 */
export function buildRepairPrompt(stackJson: string, diagnostics: string[]): string {
  return `You previously generated the tech stack diagram JSON below. Automatic layout checks measured issues with the rendered result. Repair it.

STRICT REPAIR RULES:
- Return the FULL corrected JSON (same schema, same node ids).
- Change ONLY what the diagnostics name. Do not add nodes. Do not rename ids.
- Overlaps / crossings: adjust the affected node's layout.layer / layout.orderInLayer / layout.mainPath, or REMOVE the offending low-value edge.
- Label collision: shorten the label, or drop it only when the endpoints already imply the relationship.
- Unconnected node: connect it to the component it serves with ONE meaningful edge, or delete the node entirely.
- Keep at most 12 nodes and the primary path intact.

CURRENT JSON:
${stackJson}

DIAGNOSTICS (rule — evidence — suggested fix):
${diagnostics.map((d) => `- ${d}`).join('\n')}

Return valid JSON matching the original schema.`;
}

/**
 * Centralized AI behavior configuration
 */
export const AI_BEHAVIOR: AIBehaviorConfig = {
  prompts: {
    initial: INITIAL_PROMPT_TEMPLATE,
    append: APPEND_PROMPT_TEMPLATE,
  },
  temperature: {
    initial: 0.2, // Lower temperature for more consistent results
    append: 0.3, // Slightly higher for creative additions
  },
  model: {
    gemini: 'gemini-2.5-flash',
    openai: 'gpt-4o',
    anthropic: 'claude-3-5-sonnet-20241022',
  },
};

/**
 * A compact, verified icon reference injected into the prompt so the model picks
 * known-good namespaced ids instead of hallucinating dead slugs. Built from the
 * curated POPULAR_ICONS (all CDN-verified), capped per category to keep it small.
 */
const ICON_REFERENCE: string = (() => {
  const perCategory = new Map<string, string[]>();
  const CAP = 12;
  for (const ic of POPULAR_ICONS) {
    const cat = ic.category || 'other';
    const list = perCategory.get(cat) ?? [];
    if (list.length < CAP) list.push(`${ic.title}=${ic.icon}`);
    perCategory.set(cat, list);
  }
  return [...perCategory.entries()]
    .map(([cat, items]) => `- ${cat}: ${items.join(', ')}`)
    .join('\n');
})();

/**
 * Build the initial prompt for AI generation
 */
export function buildInitialPrompt(userPrompt: string): string {
  return AI_BEHAVIOR.prompts.initial
    .replace('{{prompt}}', userPrompt)
    .replace('{{iconReference}}', ICON_REFERENCE);
}

/**
 * Build the append prompt for AI generation
 */
export function buildAppendPrompt(
  userPrompt: string,
  existingContext: {
    nodes: string;
    edges: string;
    groups: string;
    stickyNotes: string;
    contextSummary: string;
  }
): string {
  let prompt = AI_BEHAVIOR.prompts.append;

  prompt = prompt.replace('{{prompt}}', userPrompt);
  prompt = prompt.replace('{{iconReference}}', ICON_REFERENCE);
  prompt = prompt.replace('{{nodes}}', existingContext.nodes);
  prompt = prompt.replace('{{edges}}', existingContext.edges);
  prompt = prompt.replace('{{groups}}', existingContext.groups);
  prompt = prompt.replace('{{stickyNotes}}', existingContext.stickyNotes);
  prompt = prompt.replace('{{contextSummary}}', existingContext.contextSummary);
  
  return prompt;
}

/**
 * Get the temperature for the given mode
 */
export function getTemperature(isAppendMode: boolean): number {
  return isAppendMode ? AI_BEHAVIOR.temperature.append : AI_BEHAVIOR.temperature.initial;
}

/**
 * Get the model name for the given provider
 */
export function getModel(provider: keyof typeof AI_BEHAVIOR.model): string {
  return getSelectedModel(provider) || AI_BEHAVIOR.model[provider];
}
